/**
 * Empire-PWA chassis · Razorpay checkout integration.
 * See almari/src/chassis/payment.ts for architecture.
 */

const RZP_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';
const RZP_KEY_ID = 'rzp_live_SVzHpgz5EgB4bk';

export const TIER = {
  free: { name: 'Free', amount_paise: 0, period: 'forever' },
  pro: { name: 'Dial Pro', amount_paise: 99_900, period: 'year' },             // ₹999/yr
  couple: { name: 'Dial Couples', amount_paise: 199_900, period: 'year' },     // ₹1999/yr
  family: { name: 'Dial Family', amount_paise: 299_900, period: 'year' },      // ₹2999/yr
  wealth_b2b: { name: 'Wealth Concierge (B2B)', amount_paise: 1_499_900, period: 'year' }, // ₹14999/yr pilot
} as const;
export type TierId = keyof typeof TIER;

interface RzpOptions {
  key: string;
  amount: number;
  currency: 'INR';
  name: string;
  description: string;
  prefill: { email?: string; contact?: string; name?: string };
  notes: Record<string, string>;
  theme: { color: string };
  handler?: (response: { razorpay_payment_id: string }) => void;
  modal?: { ondismiss?: () => void };
}

interface RzpCtor {
  new (opts: RzpOptions): { open(): void };
}

interface WindowWithRzp extends Window {
  Razorpay?: RzpCtor;
}

let loadingPromise: Promise<void> | null = null;

async function loadScript(): Promise<void> {
  if (loadingPromise) return loadingPromise;
  loadingPromise = new Promise((resolve, reject) => {
    if ((window as WindowWithRzp).Razorpay) { resolve(); return; }
    const s = document.createElement('script');
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = (): void => resolve();
    s.onerror = (): void => reject(new Error('razorpay script failed to load'));
    document.head.appendChild(s);
  });
  return loadingPromise;
}

export interface CheckoutResult {
  readonly paymentId: string;
  readonly dismissed: boolean;
}

export async function checkout(tier: Exclude<TierId, 'free'>, prefill: { email?: string; name?: string; contact?: string } = {}): Promise<CheckoutResult> {
  await loadScript();
  const w = window as WindowWithRzp;
  if (!w.Razorpay) throw new Error('Razorpay constructor unavailable post-load');
  const t = TIER[tier];

  return new Promise((resolve) => {
    let dismissed = true;
    const opts: RzpOptions = {
      key: RZP_KEY_ID,
      amount: t.amount_paise,
      currency: 'INR',
      name: 'Dial',
      description: `${t.name} (${t.period})`,
      prefill,
      notes: { brand: 'dial', tier, app: 'dial', source: 'dial.yourtube.in' },
      theme: { color: '#5a1c1c' },
      handler: (response): void => {
        dismissed = false;
        resolve({ paymentId: response.razorpay_payment_id, dismissed: false });
      },
      modal: { ondismiss: (): void => { if (dismissed) resolve({ paymentId: '', dismissed: true }); } },
    };
    const rzp = new w.Razorpay!(opts);
    rzp.open();
  });
}

export function priceLabel(tier: TierId): string {
  if (tier === 'free') return 'Free';
  const t = TIER[tier];
  const rupees = (t.amount_paise / 100).toLocaleString('en-IN');
  return `₹${rupees}/${t.period === 'year' ? 'yr' : t.period}`;
}
